/**
 * Hot Wheels data types.
 *
 * The shape mirrors the GraphQL types generated from the schema on the Blocks
 * data gateway. Field names are PascalCase (matching the schema); values are
 * what we send/receive over the wire.
 */

export type Condition = 'Carded' | 'Loose' | 'Opened' | 'Mint';
export const CONDITION_OPTIONS: Condition[] = ['Carded', 'Loose', 'Opened', 'Mint'];

export type Priority = 'Low' | 'Medium' | 'High';
export const PRIORITY_OPTIONS: Priority[] = ['Low', 'Medium', 'High'];

/** Server-managed fields we don't write. */
interface SystemFields {
  ItemId: string;
  CreatedDate: string;
  CreatedBy: string;
  LastUpdatedDate: string;
  LastUpdatedBy: string;
  Language?: string;
  OrganizationId?: string;
  Tags?: string[];
}

/** A HotWheel record as returned by the gateway. */
export interface HotWheel extends SystemFields {
  Brand: string;
  Series: string;
  ModelNumber: string;
  ReleaseYear: number;
  Color?: string;
  Scale?: string;
  Condition?: Condition;
  PurchasePrice?: number;
  EstimatedValue?: number;
  PurchaseDate?: string;
  StorageLocation?: string;
  Description?: string;
  Favorite: boolean;
  TreasureHunt: boolean;
  SuperTreasureHunt: boolean;
  LimitedEdition: boolean;
  PublicVisibility: boolean;
  MainImage?: string;
  AdditionalImages?: string[];
}

/** Payload used by the create mutation. */
export interface HotWheelInsertInput {
  Brand: string;
  Series: string;
  ModelNumber: string;
  ReleaseYear: number;
  Color?: string;
  Scale?: string;
  Condition?: Condition;
  PurchasePrice?: number;
  EstimatedValue?: number;
  PurchaseDate?: string;
  StorageLocation?: string;
  Description?: string;
  Favorite?: boolean;
  TreasureHunt?: boolean;
  SuperTreasureHunt?: boolean;
  LimitedEdition?: boolean;
  PublicVisibility?: boolean;
  MainImage?: string;
  AdditionalImages?: string[];
}

/** Payload used by the update mutation. */
export interface HotWheelUpdateInput {
  Brand?: string;
  Series?: string;
  ModelNumber?: string;
  ReleaseYear?: number;
  Color?: string;
  Scale?: string;
  Condition?: Condition;
  PurchasePrice?: number;
  EstimatedValue?: number;
  PurchaseDate?: string;
  StorageLocation?: string;
  Description?: string;
  Favorite?: boolean;
  TreasureHunt?: boolean;
  SuperTreasureHunt?: boolean;
  LimitedEdition?: boolean;
  PublicVisibility?: boolean;
  MainImage?: string;
  AdditionalImages?: string[];
}

export interface HotWheelDeleteInput {
  ItemId: string;
  isHardDelete: boolean;
}

/** Paged response wrapper. */
export interface PagedHotWheels {
  items: HotWheel[];
  totalCount: number;
  totalPages: number;
  pageNo: number;
  pageSize: number;
  hasNextPage: boolean;
  hasPreviousPage: boolean;
}

/** A wishlist entry. */
export interface WishlistItem extends SystemFields {
  CarName: string;
  Brand?: string;
  Series?: string;
  Priority?: Priority;
  Notes?: string;
  Acquired?: boolean;
}

export interface WishlistInsertInput {
  CarName: string;
  Brand?: string;
  Series?: string;
  Priority?: Priority;
  Notes?: string;
  Acquired?: boolean;
}

export interface WishlistUpdateInput {
  CarName?: string;
  Brand?: string;
  Series?: string;
  Priority?: Priority;
  Notes?: string;
  Acquired?: boolean;
}

export interface PagedWishlist {
  items: WishlistItem[];
  totalCount: number;
  totalPages: number;
  pageNo: number;
  pageSize: number;
  hasNextPage: boolean;
  hasPreviousPage: boolean;
}

/** Common order-by options for the collection list. */
export type SortField =
  | 'Brand'
  | 'Series'
  | 'ModelNumber'
  | 'ReleaseYear'
  | 'PurchasePrice'
  | 'EstimatedValue'
  | 'PurchaseDate'
  | 'CreatedDate';
export type SortDir = 'ASC' | 'DESC';